import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import { Button } from 'ui-components/components/button';
import { FancyStateIcon } from 'ui-components/components/fancy-state-icon';
import { Modal } from 'ui-components/components/modal';
import { ModalCloseButton } from 'ui-components/components/modal/modal-close-button';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';

import { emptyArray } from 'ui-components/default-props/empty';
import { createLookupHash } from '../../../services/hash-service';
import { OrderItemPropType } from '../../../prop-types/order-item';
import ItemDisplay from '../../../components/item-display';

import style from './error-modal.module.less';

const messages = defineMessages({
  warningTitle: {
    description: 'inventoryAllocation.warningTitle',
    defaultMessage: 'Inventory Warning',
    id: 'T2cMHL',
  },
  errorTitle: {
    description: 'inventoryAllocation.errorTitle',
    defaultMessage: 'Inventory Unavailable',
    id: 'd8mNLS',
  },
  insufficientInventoryMessage: {
    description: 'inventoryAllocation.insufficientInventory',
    defaultMessage: 'Insufficient inventory to fulfill these items:',
    id: 'wnRkYA',
  },
  cancel: {
    description: 'inventoryAllocation.cancel',
    defaultMessage: 'Cancel',
    id: 'DcpPgX',
  },
  continue: {
    description: 'inventoryAllocation.continue',
    defaultMessage: 'Continue',
    id: 'BarFJy',
  },
  additionalItems: {
    description: 'inventoryAllocation.additionalItems',
    defaultMessage: '+ {itemCount} more',
    id: 'V23/56',
  },
});

export const InventoryAllocationErrorModal = ({
  inventoryLevels = emptyArray,
  items,
  isWarning = false,
  modalFrameworkMethods,
  onContinue,
}) => {
  const { closeModal } = modalFrameworkMethods;
  const titleMessage = isWarning ? messages.warningTitle : messages.errorTitle;

  const inventoryLevelsMap = useMemo(() => createLookupHash(inventoryLevels, 'fulfillmentPlanItemId'), [inventoryLevels]);

  const insufficientStockItems = items.filter((item) => {
    const itemInventoryInfo = inventoryLevelsMap[item.fulfillmentPlanItemId] ?? {};
    return itemInventoryInfo.hasInsufficientStock || itemInventoryInfo.hasNoInventory;
  });
  const continueWithoutAllocating = () => {
    if (onContinue) {
      onContinue();
    }
    closeModal();
  };

  return (
    <Modal
      className={classnames(style['error-modal'], {
        [style.warning]: isWarning,
      })}
    >
      <ModalBody className={style['error-modal-body']}>
        <div className={style.error}>
          <div className={style['title-row']}>
            <div className={style['state-icon-wrapper']}>
              <FancyStateIcon state={isWarning ? 'warning' : 'error'} />
            </div>
            <div className={style['title-text']}>
              <FormattedMessage {...titleMessage} />
            </div>
            <div className={style['close-button-wrapper']}>
              <ModalCloseButton closeModal={closeModal} />
            </div>
          </div>
          <div className={style.message}>
            <FormattedMessage {...messages.insufficientInventoryMessage} />
          </div>
        </div>
        <div className={style['item-list']}>
          {insufficientStockItems.slice(0, 3).map((item, index) => (
            <ItemDisplay key={index} className={style['item-display']} item={{ ...item, quantityOrdered: item.quantity }} layout='small' />
          ))}
          {insufficientStockItems.length > 3 && (
            <div className={style['additional-items']}>
              <FormattedMessage {...messages.additionalItems} values={{ itemCount: items.length - 3 }} />
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter className={style['modal-footer']}>
        <Button
          onClick={() => {
            closeModal();
          }}
          theme='tertiary'
        >
          <FormattedMessage {...messages.cancel} />
        </Button>
        {isWarning && onContinue && (
          <Button
            className={style['continue-button']}
            onClick={() => {
              continueWithoutAllocating();
            }}
            theme='primary'
          >
            <FormattedMessage {...messages.continue} />
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

InventoryAllocationErrorModal.propTypes = {
  inventoryLevels: PropTypes.array,
  items: PropTypes.arrayOf(OrderItemPropType).isRequired,
  isWarning: PropTypes.bool,
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  onContinue: PropTypes.func,
};
